import { useMemo } from 'react'
import { QueryClient, useQueryClient } from '@tanstack/react-query'
import { chatConversationsQueryKey, chatRoomQueries } from '../../../api/chat'
import { MessageRow, RoomHeader } from '../../../generated/api/chat-room'
import { RoomMessagesData, insertMessage } from './roomMessagesCache'

export const insertMessageIntoCache = (
  client: QueryClient,
  row: MessageRow
): void => {
  const { queryKey } = chatRoomQueries.messages(row.roomId)
  const state = client.getQueryState<RoomMessagesData>(queryKey)
  if (state === undefined) return

  client.setQueryData<RoomMessagesData>(queryKey, (old) =>
    insertMessage(old, row)
  )
  if (state.fetchStatus === 'fetching') {
    client.invalidateQueries({ queryKey, exact: true })
  }
}

export const removeRoomQueries = (
  client: QueryClient,
  roomId: string
): void => {
  client.removeQueries({
    queryKey: chatRoomQueries.header(roomId).queryKey,
    exact: true,
  })
  client.removeQueries({
    queryKey: chatRoomQueries.messages(roomId).queryKey,
    exact: true,
  })
  client.removeQueries({
    queryKey: chatRoomQueries.reads(roomId).queryKey,
    exact: true,
  })
  client.removeQueries({ queryKey: chatRoomQueries.members(roomId).queryKey })
  client.removeQueries({
    queryKey: [{ scope: 'chat', entity: 'roomMemberCandidates', roomId }],
  })
}

export const invalidateRoomSettingsQueries = (
  client: QueryClient,
  roomId: string
): void => {
  client.invalidateQueries({
    queryKey: chatRoomQueries.header(roomId).queryKey,
  })
  client.invalidateQueries({
    queryKey: chatRoomQueries.members(roomId).queryKey,
  })
  client.invalidateQueries({
    queryKey: [{ scope: 'chat', entity: 'roomMemberCandidates', roomId }],
  })
  client.invalidateQueries({ queryKey: chatConversationsQueryKey })
}

export const invalidateRoomQueries = (
  client: QueryClient,
  roomId: string
): void => {
  invalidateRoomSettingsQueries(client, roomId)
  client.invalidateQueries({
    queryKey: chatRoomQueries.messages(roomId).queryKey,
  })
  client.invalidateQueries({ queryKey: chatRoomQueries.reads(roomId).queryKey })
}

export const invalidateRoomReads = (
  client: QueryClient,
  roomId: string
): void => {
  client.invalidateQueries({ queryKey: chatRoomQueries.reads(roomId).queryKey })
  client.invalidateQueries({
    queryKey: [{ scope: 'chat', entity: 'roomReaders', roomId }],
  })
}

export const applyRoomHeader = (
  client: QueryClient,
  header: RoomHeader
): void => {
  client.setQueryData(chatRoomQueries.header(header.id).queryKey, header)
  client.invalidateQueries({
    queryKey: chatRoomQueries.members(header.id).queryKey,
  })
  client.invalidateQueries({
    queryKey: [
      { scope: 'chat', entity: 'roomMemberCandidates', roomId: header.id },
    ],
  })
  client.invalidateQueries({ queryKey: chatConversationsQueryKey })
}

interface RoomMessagesCache {
  insert: (row: MessageRow) => void
  removeRoom: (roomId: string) => void
  invalidateRoom: (roomId: string) => void
  applyHeader: (header: RoomHeader) => void
}

export const useRoomMessagesCache = (): RoomMessagesCache => {
  const client = useQueryClient()
  return useMemo(
    () => ({
      insert: (row) => insertMessageIntoCache(client, row),
      removeRoom: (roomId) => removeRoomQueries(client, roomId),
      invalidateRoom: (roomId) => invalidateRoomQueries(client, roomId),
      applyHeader: (header) => applyRoomHeader(client, header),
    }),
    [client]
  )
}
